import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import 'leaflet/dist/leaflet.css';
import { useDataFromS3 } from '../hooks/useDataFromS3';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MapRiskMax from '../components/map_risk_rain';
import ScatterRisk from '../components/scatter_risk';
import HeatmapRisk from '../components/heatmap_risk';
import CommonCard from '../item/common_card';
import PageLayout from '../item/page_layout';
import Progress from '../item/progress';
import { RISK_HEATMAP_COLOR, SOIL_RISK_COLOR } from '../const/color';
import { H1_RISK_LEGEND } from '../const/legend';
import { BASE_LAYER_LIST } from '../const/map_config';
import { setSrRiskColor, setMapRiskColor } from '../func/color';
import { getTimeAry, convertAreaDataFromMshData, convertCityDataFromMshData, getAllByCode, getPolygonOrMultiPolygonCenter, convertCityDataRiskFromMshData, getAllByDateTimeWithCityInfo, getRiskH1MaxByCode, filterDataByCodeAry, convertAreaDataSrRainFromMshData, convertAreaDataScatterFromAreaData, convertCityDataScatterFromAreaData, convertAreaDataScatterFromMshData } from '../func/util';
import '../style/legend_risk.css';
import choropleth_area from '../data/area.json';
import choropleth_msh from '../data/msh.json';

const Risk = (props) => {
    const [selectLayer, setSelectLayer] = useState(
        BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1]
    );

    // 市町村、字、メッシュ、個別の選択をクリアするかしないかのフラグ
    const [shouldClearSelect, setShouldClearSelect] = useState(false)

    // 地図（市町村・字・メッシュ）
    const [cityLandslideRiskH1, setCityLandslideRiskH1] = useState(null);
    const [areaLandslideRiskH1, setAreaLandslideRiskH1] = useState(null);
    const [mshLandslideRiskH1, setMshLandslideRiskH1] = useState(null);
    // ヒートマップ
    const [landslideAllRiskDatetime, setLandslideAllRiskDatetime] = useState(null);
    // 散布図
    const [riskScatterAllDatetimeArea, setRiskScatterAllDatetimeArea] = useState(null);
    const [riskScatterAllDatetimeCity, setRiskScatterAllDatetimeCity] = useState(null);
    // ツールチップ
    const [tooltipInfo, setTooltipInfo] = useState(null);

    const [dateArray, setDateArray] = useState(null);
    const [shouldFetchMain, setShouldFetchMain] = useState(false);

    const prevDataARef = useRef(null);

    // 全データ再取得の要否を定期監視する
    const {
        data: datetimeLatest,
        loading: datetimeLatestLoading,
        error: datetimeLatestError,
    } = useDataFromS3(`api/get_datetime_latest`);

    // サーバーの最終更新日時が変更されているか判定
    useEffect(() => {
        if (datetimeLatest && JSON.stringify(datetimeLatest) !== JSON.stringify(prevDataARef.current)) {
            prevDataARef.current = datetimeLatest;
            setShouldFetchMain(true); // dataが変わったらメインデータを取得
        } else {
            setShouldFetchMain(false); // dataが変わってないならメインデータを取得しない
        }
    }, [datetimeLatest]);

    let rain_mtr_url = null
    let risk_status_url = null

    if (shouldFetchMain) {
        rain_mtr_url = `api/get_risk`;
        risk_status_url = `api/get_risk_status`;
    }

    // 土壌雨量指数情報を取得
    const {
        data: rainMtrData,
        loading: rainMtrLoading,
        error: rainMtrError,
    } = useDataFromS3(rain_mtr_url);

    // 災害情報を取得
    const {
        data: riskStatusData,
        loading: riskStatusLoading,
        error: riskStatusError
    } = useDataFromS3(risk_status_url);

    /**
     * Returns display label for soil risk level
     * @param {number} level - soil risk level value
     * @returns {string} label text for tooltip
     */
    const getLabelTooltipSoilLevel = (level) => {
        const key = `LEVEL_${level}`;
        return SOIL_RISK_COLOR[key]?.label ?? '';
    };

    /**
     * Handles click event on scatter point
     * - Moves map center to selected point
     * - Updates selection based on point type (area or city)
     *
     * @param {Object} point - scatter point meta data
     */
    // 散布図クリック時の動作
    const scatterClickEvent = (series, seriesIndex, point) => {
        props.setRiskMapCenter(series[seriesIndex].arealng, series[seriesIndex].arealat)
        setShouldClearSelect(false)
        setSelectLayer(BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1])
        props.setSelectMsh([]);
        if (point?.meta.type === "area") {
            props.setSelectSubCity([]);
            props.setSelectArea([point?.meta.area_code[0]]);
        }
        if (point?.meta.type === "city") {
            props.setSelectSubCity([point?.meta.city_code]);
            props.setSelectArea([]);
        }
    }

    /**
     * Generates color array for scatter chart points
     * - Supports slider-based filtering
     *
     * @param {Array} chartData - scatter source data
     * @param {boolean} isSlider - slider enabled flag
     * @param {Array} value - slider range [min, max]
     * @returns {Array<string>} color list
     */
    // 散布図のカラー
    const soilRainGetColors = (chartData, isSlider, value) => {
        let res = []
        if (isSlider) {
            for (let i = value[1]; i > value[0]; i--) {
                if (chartData?.length - 1 > i) {
                    res.push(setSrRiskColor(chartData[i]?.soil_index_level).color)
                }
            }
        } else {
            if (chartData?.length > 0) {
                chartData.map(item => {
                    res.push(setSrRiskColor(item?.soil_index_level).color)
                })
            }
        }
        return res;
    }

    // 散布図の1時間雨量値
    const soil_rain_h1_ary = (scatterData) => {
        return scatterData.map((obj) => obj.h1_h24_val[0]);
    }

    // 散布図の24時間雨量値
    const soil_rain_h24_ary = (scatterData) => {
        return scatterData.map((obj) => obj.h1_h24_val[1]);
    }

    // 散布図の土壌雨量指数
    const soil_rain_dc_tank_ary = (scatterData) => {
        return scatterData.map((obj) => obj.h1_dc_tank_of_val[1]);
    }

    /**
     * Convert raw area risk data into ApexCharts heatmap series format
     * - Sorts by city_code (descending)
     * - Converts 0 values to a very small number to keep color rendering
     *
     * @param {Array} date_ary - X-axis datetime array
     * @param {Array} area_val_ary - area-based risk values
     * @param {boolean} isOver - unused (can be removed if not needed)
     * @returns {Array} Heatmap series data
     */
    // ヒートマップデータ取得
    const getHeatmapData = (date_ary, area_val_ary, isOver) => {
        let sorted_ary = []
        if (area_val_ary?.length > 1) {
            sorted_ary = [...area_val_ary].sort((a, b) => (b.city_code - a.city_code));
        } else {
            sorted_ary = area_val_ary
        }
        const chartData = sorted_ary.map((item) => ({
            name: item?.city_name,
            data: item?.risk_val.map((val, i) => ({
                x: date_ary[i],
                y: val === 0 ? 0.0001 : val,
                value: val,
                name: val,
                soil_index_level: val,
                city_code: item?.city_code,
                city_name: item?.city_name,
                area_lat: item?.area_lat,
                area_lng: item?.area_lng
            }))
        }));
        return chartData;
    }

    /**
     * Handles click event on heatmap cell
     * - Centers map to selected city
     * - Resets selection state
     * - Switches map layer to default
     *
     * @param {Object} clickedItem - clicked heatmap cell data
     */
    // ヒートマップクリック時の動作
    const heatmapClickEvent = (clickedItem) => {
        const cityCode = clickedItem.city_code;
        props.setBsnMapCenter([clickedItem.area_lng, clickedItem.area_lat])
        props.setRiskMapCenter([clickedItem.area_lng, clickedItem.area_lat])
        setShouldClearSelect(false)
        setSelectLayer(BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1])
        props.setSelectArea([])
        props.setSelectSubCity([cityCode]);
        props.setSelectMsh([]);
        props.setSelectBsn([]);
    }

    // ヒートマップのカラー
    const colorScale = () => {
        return {
            ranges: RISK_HEATMAP_COLOR
        }
    }

    // ツールチップ情報行コンポーネント
    const InfoRow = ({ label, value }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography fontSize={13}>{label}</Typography>
            <Typography fontSize={13} fontWeight="bold">{value}</Typography>
        </Box>
    );

    // ツールチップ内容レンダリング関数
    const renderTooltipBody = (tooltipKey, tooltipInfo) => {
        if (!tooltipInfo?.data) return null;
        const d = tooltipInfo.data;
        switch (tooltipKey) {
            case 'scatter_rain_h1_dc_tank':
                return (<>
                    <InfoRow label="土壌雨量指数" value={d.h1_dc_tank_of_val?.[1]} />
                    <InfoRow label="1時間雨量" value={d.h1_dc_tank_of_val?.[0]} />
                    <InfoRow label="警戒基準相当" value={d.soil_index_level} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                );

            case 'scatter_rain_h1_h24':
                return (<>
                    <InfoRow label="24時間雨量" value={d.h1_h24_val?.[1]} />
                    <InfoRow label="1時間雨量" value={d.h1_h24_val?.[0]} />
                    <InfoRow label="警戒基準相当" value={d.soil_index_level} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                );
            case 'scatter_rain_h1_dc_tank_city':
                return (<>
                    <InfoRow label="土壌雨量指数" value={d.h1_dc_tank_of_val[1]} />
                    <InfoRow label="1時間雨量" value={d.h1_dc_tank_of_val[0]} />
                    <InfoRow label="土砂災害発生リスク" value={d.risk_val} />
                    <InfoRow label="警報基準相当" value={d.soil_index_level} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                )
            case 'heatmap_rain_city':
                return (<>
                    <InfoRow label="日時" value={d.x} />
                    <InfoRow label="市町村名" value={d.city_name} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                );

            default:
                return null;
        }
    };

    /**
     * Builds ApexCharts scatter series (H1 vs H24)
     * - Groups points by risk color
     *
     * @param {Array} chartData
     * @param {boolean} isSlider
     * @param {Array} value
     * @returns {Array}
     */
    const soilRainGetSeriesH1H24 = (chartData, isSlider, value) => {
        if (!chartData?.length) return [];
        const filteredData = isSlider ? chartData.slice(value[0], value[1] + 1) : chartData;
        const colorGroups = {};
        filteredData.forEach(item => {
            const [y, x] = item.h1_h24_val;
            const color = setSrRiskColor(item?.soil_index_level).color;

            if (!colorGroups[color]) {
                colorGroups[color] = {
                    name: item.name,
                    data: [],
                    color: color
                };
            }

            colorGroups[color].data.push({
                x, y,
                meta: {
                    name: item.name,
                    city_name: item.city_name,
                    area_code: item.area_code,
                    area_name: item.name,
                    area_lat: item.area_lat,
                    area_lng: item.area_lng,
                    soil_index_level: item.soil_index_level,
                    h1_h24_val: item.h1_h24_val,
                    type: "area"
                }
            });
        });
        return Object.values(colorGroups);
    };

    /**
     * Builds city-level scatter series (H1 vs DC Tank)
     * - Adds dynamic marker size by risk level
     *
     * @param {Array} chartData
     * @param {boolean} isSlider
     * @param {Array} value
     * @returns {Array}
     */
    const soilRainGetSeriesH1DcTank = (chartData, isSlider, value) => {
        if (!chartData?.length) return [];
        const filteredData = isSlider ? chartData.slice(value[0], value[1] + 1) : chartData;
        const colorGroups = {};
        filteredData.forEach(item => {
            const [y, x] = item.h1_dc_tank_of_val;
            const color = setSrRiskColor(item?.soil_index_level).color;

            if (!colorGroups[color]) {
                colorGroups[color] = {
                    name: item.name,
                    data: [],
                    color: color,
                };
            }

            colorGroups[color].data.push({
                x, y,
                meta: {
                    name: item.name,
                    data: [[x, y]],
                    city_name: item.city_name,
                    area_code: item.area_code,
                    arealng: item.area_lng,
                    arealat: item.area_lat,
                    soil_index_level: item.soil_index_level,
                    h1_dc_tank_of_val: item.h1_dc_tank_of_val,
                    type: "area"
                }
            });
        });
        return Object.values(colorGroups);
    };

    /**
     * Builds city-level scatter series (H1 vs DC Tank of City)
     * - Adds dynamic marker size by risk level
     *
     * @param {Array} chartData
     * @param {boolean} isSlider
     * @param {Array} value
     * @returns {Array}
     */
    const soilRainGetSeriesH1DcTankCity = (chartData, isSlider, value) => {
        if (!chartData?.length) return [];
        const filteredData = isSlider ? chartData.slice(value[0], value[1] + 1) : chartData;
        const groups = {};
        filteredData.forEach(item => {
            const [y, x] = item.h1_dc_tank_of_val;
            const color = setSrRiskColor(item?.soil_index_level)?.color;
            const sizePoint = setSrRiskColor(item?.soil_index_level)?.size_point;
            const key = item.city_code ?? item.name; // group by city_code (fallback to name)
            if (!groups[key]) {
                groups[key] = {
                    name: item.name,
                    data: [],
                    color: color,
                    marker: {
                        size: sizePoint
                    }
                };
            }

            groups[key].data.push({
                x, y,
                meta: {
                    name: item.name,
                    area_lat: item.area_lat,
                    area_lng: item.area_lng,
                    city_code: item.city_code,
                    city_name: item.city_name,
                    soil_index_level: item.soil_index_level,
                    h1_dc_tank_of_val: item.h1_dc_tank_of_val,
                    risk_val: item.risk_val,
                    type: "city"
                }
            });
        });
        return Object.values(groups);
    };

    // データ整備用のパラメータを取得
    const getParams = (keyAry, keyName = '') => {
        const features_area = choropleth_area.features
        const features_msh = choropleth_msh.features

        const stringKeyAry = keyAry.flat().map(k => String(k));
        const numericKeyAry = stringKeyAry.map(k => Number(k)); // city_code比較用

        // メッシュデータ取得（keyAryに含まれるコードを持つメッシュをすべて取得）
        let allFilteredMsh = features_msh.filter(f => {
            const geoCode = f.properties[keyName];
            if (Array.isArray(geoCode)) {
                return geoCode.some(c =>
                    keyName === 'city_code'
                        ? numericKeyAry.includes(Number(c))
                        : stringKeyAry.includes(String(c))
                );
            }
            return keyName === 'city_code'
                ? numericKeyAry.includes(Number(geoCode))
                : stringKeyAry.includes(String(geoCode));
        });

        // 選択時（keyAryが1つなど）か、初期表示（全域）かを判定
        // クリック時は keyAry の長さが 1 になることを利用して分岐
        let masterMshFeatures;
        let masterAreaFeature = [];

        if (keyAry.length === 1 && allFilteredMsh.length > 0 && keyName !== 'city_code') {
            // --- 地図クリック・地域選択時 ---
            const selectedTargetCode = keyAry[0];

            // クリックされた字コードに該当するデータを取得
            masterAreaFeature = features_area.filter(f =>
                String(f.properties.id) === String(selectedTargetCode)
            );

            // メッシュの情報をベースに、area_code を「選択されたコード1つ」に書き換える
            masterMshFeatures = allFilteredMsh.map(msh => ({
                ...msh,
                properties: {
                    ...msh.properties,
                    area_code: [selectedTargetCode]
                }
            }));
        } else {
            // --- 初期表示（全域）または複数選択 ---
            masterMshFeatures = allFilteredMsh;

            // masterAreaFeature: 複数選択時や初期表示でも参照できるように作成
            // keyAry が市コード（city_code）の場合は city_code を使ってフィルタ
            if (Array.isArray(keyAry) && keyAry.length > 0) {
                masterAreaFeature = features_area.filter(f => {
                    const pid = f.properties && f.properties.id;
                    const pcity = f.properties && f.properties.city_code;
                    return (
                        (pid != null && (keyAry.includes(pid) || keyAry.includes(String(pid)))) ||
                        (pcity != null && (keyAry.includes(pcity) || keyAry.includes(String(pcity))))
                    );
                });
            } else {
                masterAreaFeature = [];
            }
        }

        // メッシュデータの properties.id を配列で取得(これが対象のメッシュコード)
        const msh_code_ary = masterMshFeatures.map(feature => feature.properties.id);

        return {
            msh_code_ary,
            masterMshFeatures,
            masterAreaFeature
        };
    };

    // ローディング開始
    const startLoading = () => {
        props.setReady(prev => {
            const updated = Object.fromEntries(
                Object.keys(prev).map(key => [key, false])
            );
            return updated;
        });
    }

    useEffect(() => {
        // ローディング開始
        startLoading()
        const fetchData = async () => {
            let params_obj = {}
            let date_ary = []
            let msh_landslide_risk_all_datetime = [];

            let city_landslide_risk_all_datetime = [];
            let area_landslide_risk_all_datetime = [];
            let landslide_risk_all_datetime = [];

            let area_landslide_risk_h1_max = [];
            let city_landslide_risk_h1_max = [];
            let msh_landslide_risk_h1_max = [];
            let risk_scatter_all_datetime_area = [];
            let risk_scatter_all_datetime_city = [];

            let msh_sr_rain_all_data = []
            let area_sr_rain_all_data = []

            if (props.selectArea && Array.isArray(props.selectArea)
                && props.selectArea.length > 0) {
                params_obj = getParams(props.selectArea, 'area_code')
            } else if (props.selectMsh && Array.isArray(props.selectMsh)
                && props.selectMsh.length > 0) {
                params_obj = getParams(props.selectMsh, 'id')
            } else if (props.selectSubCity && Array.isArray(props.selectSubCity)
                && props.selectSubCity.length > 0) {
                params_obj = getParams(props.selectSubCity, 'city_code')
            } else {
                params_obj = getParams(props.selectCitys, 'city_code')
            }

            if (Object.keys(params_obj).length > 0) {
                // 日時の配列を取得
                date_ary = getTimeAry(datetimeLatest, false)

                msh_landslide_risk_all_datetime = getAllByCode(params_obj.msh_code_ary, rainMtrData, props.h6Switch)
                if (msh_landslide_risk_all_datetime.length > 0) {
                    // 字コードごとのリスクと雨量のオブジェクト配列を取得
                    area_landslide_risk_all_datetime = convertAreaDataFromMshData(params_obj.masterMshFeatures, msh_landslide_risk_all_datetime, params_obj.masterAreaFeature)
                    // 地図の字レイヤー用のデータ
                    area_landslide_risk_h1_max = getRiskH1MaxByCode(area_landslide_risk_all_datetime, 0)
                    // // 地図の市町村レイヤー用のデータ
                    city_landslide_risk_h1_max = getRiskH1MaxByCode(area_landslide_risk_all_datetime, 5)
                    // 地図のメッシュレイヤー用のデータ
                    msh_landslide_risk_h1_max = getRiskH1MaxByCode(msh_landslide_risk_all_datetime, 0)
                    // ヒートマップの全日時断面用のデータ
                    city_landslide_risk_all_datetime = convertCityDataFromMshData(params_obj.masterMshFeatures, msh_landslide_risk_all_datetime)
                    // ヒートマップの全日時断面用のデータ
                    landslide_risk_all_datetime = getAllByDateTimeWithCityInfo(params_obj.masterMshFeatures, city_landslide_risk_all_datetime);
                }

                // 散布図用のデータを取得
                msh_sr_rain_all_data = filterDataByCodeAry(params_obj.msh_code_ary, rainMtrData)
                area_sr_rain_all_data = convertAreaDataSrRainFromMshData(params_obj.masterMshFeatures, msh_sr_rain_all_data, params_obj.masterAreaFeature)
                if (area_sr_rain_all_data.length > 0) {
                    // 字ごと（1時間雨量(mm/h)、土壌雨量指数）の散布図データ
                    risk_scatter_all_datetime_area = convertAreaDataScatterFromAreaData(params_obj.masterMshFeatures, area_sr_rain_all_data)
                    // 市町村ごと（1時間雨量(mm/h)、土壌雨量指数）の散布図データ
                    risk_scatter_all_datetime_city = convertCityDataScatterFromAreaData(params_obj.masterMshFeatures, area_sr_rain_all_data)
                }
                else{
                    // 字ごと（1時間雨量(mm/h)、土壌雨量指数）の散布図データ
                    risk_scatter_all_datetime_area = convertAreaDataScatterFromMshData(params_obj.masterMshFeatures, msh_sr_rain_all_data)
                    // 市町村ごと（1時間雨量(mm/h)、土壌雨量指数）の散布図データ
                    risk_scatter_all_datetime_city = convertCityDataRiskFromMshData(params_obj.masterMshFeatures, msh_sr_rain_all_data)
                }
            }

            // 市町境のデータを除外
            // 地図の市町村レイヤー用のデータ (5桁でチェック)
            const filteredCityMax = city_landslide_risk_h1_max.filter(item => 
                props.selectCitys.includes(String(item.code))
            );

            // 地図の字レイヤー用のデータ (上5桁が選択中の市町村かチェック)
            const filteredAreaMax = area_landslide_risk_h1_max.filter(item => {
                const parentCityCode = String(Math.floor(Number(item.code) / 10000)); // 数値→文字列
                return props.selectCitys.includes(parentCityCode);
            });

            // レイヤー用データセット
            setCityLandslideRiskH1(filteredCityMax?.length > 0 ? filteredCityMax : null)
            setAreaLandslideRiskH1(filteredAreaMax?.length > 0 ? filteredAreaMax : null)
            setMshLandslideRiskH1(msh_landslide_risk_h1_max?.length > 0 ? msh_landslide_risk_h1_max : null)

            // 散布図用データ
            setRiskScatterAllDatetimeArea(risk_scatter_all_datetime_area?.length > 0 ? risk_scatter_all_datetime_area : null)
            setRiskScatterAllDatetimeCity(risk_scatter_all_datetime_city?.length > 0 ? risk_scatter_all_datetime_city : null)

            // ヒートマップ用データ
            setLandslideAllRiskDatetime(landslide_risk_all_datetime?.length > 0 ? landslide_risk_all_datetime : null)

            // 日時
            setDateArray(date_ary.length > 0 ? date_ary : null)
        };
        if (datetimeLatest && rainMtrData && riskStatusData) {
            fetchData();
        }
    }, [datetimeLatest,
        rainMtrData,
        riskStatusData,
        props.selectCitys, props.selectSubCity, props.selectArea, props.selectMsh, props.selectBsn,
        props.h6Switch
    ]);

    if (datetimeLatestLoading || datetimeLatest === undefined
        || (datetimeLatestError && datetimeLatestError.message.includes("Received HTML"))
        || rainMtrLoading || rainMtrData === undefined
        || (rainMtrError && rainMtrError.message.includes("Received HTML"))
        || riskStatusLoading || riskStatusData === undefined
        || (riskStatusError && riskStatusError.message.includes("Received HTML"))
    ) {
        return <Progress />
    }

    if (datetimeLatestError
        || rainMtrError
        || riskStatusError
    ) {
        return (
            <>
                <Typography
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: 'white'
                    }}
                >
                    申し訳ございません、データ取得に失敗しました。
                </Typography>
                <Typography
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: 'white'
                    }}
                >
                    恐れ入りますが、しばらく時間をおいてから再度お試しください。
                </Typography>
            </>
        )
    }

    return (
        <>
            <PageLayout
                riskStatus={riskStatusData[0].status}
                dateTime={datetimeLatest && datetimeLatest[12]}
            >
                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ height: { xs: 'auto', sm: '50%' } }}>
                    <CommonCard
                        child={
                            <MapRiskMax
                                selectSubCity={props.selectSubCity}
                                setSelectSubCity={props.setSelectSubCity}
                                selectArea={props.selectArea}
                                setSelectArea={props.setSelectArea}
                                selectMsh={props.selectMsh}
                                setSelectMsh={props.setSelectMsh}
                                selectBsn={props.selectBsn}
                                setSelectBsn={props.setSelectBsn}
                                selectCitys={props.selectCitys}
                                cityLandslideRiskH1={cityLandslideRiskH1}
                                areaLandslideRiskH1={areaLandslideRiskH1}
                                mshLandslideRiskH1={mshLandslideRiskH1}
                                riskMapZoom={props.riskMapZoom}
                                riskMapCenter={props.riskMapCenter}
                                selectLayer={selectLayer}
                                setSelectLayer={setSelectLayer}
                                setRiskColor={(risk) => setMapRiskColor(SOIL_RISK_COLOR, risk)}
                                shouldClearSelect={shouldClearSelect}
                                setShouldClearSelect={setShouldClearSelect}
                                tooltipLabel={"警報基準相当"}
                                legendLabel={"警報基準相当"}
                                pageType={'risk'}
                            />
                        }
                        title={"警報基準相当"}
                        xsHeight="300px"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ height: { xs: 'auto', sm: '50%' } }}>
                    <Stack spacing={0} sx={{ height: { xs: 'auto', sm: '100%' } }}>
                        {/* 字名ごとの1時間雨量,土壤雨量指数 */}
                        <CommonCard
                            child={
                                <>
                                    {H1_RISK_LEGEND()}
                                    <ScatterRisk
                                        dateArray={dateArray}
                                        distance={riskScatterAllDatetimeArea?.length}
                                        scatterData={riskScatterAllDatetimeArea}
                                        getSeries={soilRainGetSeriesH1DcTank}
                                        x_ary={soil_rain_dc_tank_ary}
                                        y_ary={soil_rain_h1_ary}
                                        maxValX={200}
                                        maxValY={30}
                                        isShowLabelMaxXMm={false}
                                        scatterClickEvent={scatterClickEvent}
                                        setTooltipInfo={setTooltipInfo}
                                        tooltipInfo={tooltipInfo}
                                        renderBody={renderTooltipBody}
                                        isReadyKey={['scatter_rain_h1_dc_tank']}
                                        isReadyFlg={props.isReady.scatter_rain_h1_dc_tank}
                                        setReady={props.setReady}
                                        selectCitys={props.selectCitys}
                                        isShowDataLabel={false}
                                        height={'calc(100% - 20px)'}
                                        textX={"土壌雨量指数"}
                                        textY={"1時間雨量"}
                                        whiteSpace={'normal'}
                                        tooltipKey={"scatter_rain_h1_dc_tank"}
                                    />
                                </>
                            }
                            title={"字名ごとの1時間雨量,土壤雨量指数"}
                            xsHeight="300px"
                        />
                    </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ height: { xs: 'auto', sm: '50%' } }}>
                    <Stack spacing={0} sx={{ height: { xs: 'auto', sm: '100%' } }}>
                        {/* 字名ごとの1時間雨量,24時間雨量 */}
                        <CommonCard
                            child={
                                <>
                                    {H1_RISK_LEGEND()}
                                    <ScatterRisk
                                        dateArray={dateArray}
                                        distance={riskScatterAllDatetimeArea?.length}
                                        scatterData={riskScatterAllDatetimeArea}
                                        getSeries={soilRainGetSeriesH1H24}
                                        getColors={soilRainGetColors}
                                        x_ary={soil_rain_h24_ary}
                                        y_ary={soil_rain_h1_ary}
                                        maxValX={200}
                                        maxValY={30}
                                        scatterClickEvent={scatterClickEvent}
                                        setTooltipInfo={setTooltipInfo}
                                        tooltipInfo={tooltipInfo}
                                        renderBody={renderTooltipBody}
                                        isReadyKey={['scatter_rain_h1_h24']}
                                        isReadyFlg={props.isReady.scatter_rain_h1_h24}
                                        setReady={props.setReady}
                                        selectCitys={props.selectCitys}
                                        isShowDataLabel={false}
                                        height={'calc(100% - 20px)'}
                                        textX={"24時間雨量"}
                                        textY={"1時間雨量"}
                                        whiteSpace={'normal'}
                                        tooltipKey={"scatter_rain_h1_h24"}
                                    />
                                </>
                            }
                            title={"字名ごとの1時間雨量,24時間雨量"}
                            xsHeight="300px"
                        />
                    </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 9 }} sx={{ height: { xs: 'auto', sm: '50%' } }}>
                    <CommonCard
                        child={
                            <HeatmapRisk
                                distance={18}
                                limitDataCount={200}
                                defaultUnit={props.defaultUnit}
                                dateArray={dateArray}
                                allRiskDatetime={landslideAllRiskDatetime}
                                getHeatmapData={getHeatmapData}
                                heatmapClickEvent={heatmapClickEvent}
                                colorScale={colorScale}
                                setTooltipInfo={setTooltipInfo}
                                tooltipInfo={tooltipInfo}
                                renderBody={renderTooltipBody}
                                tooltipKey={"heatmap_rain_city"}
                                isReadyKey={['heatmap_rain_city']}
                                isReadyFlg={props.isReady.heatmap_rain_city}
                                setReady={props.setReady}
                            />
                        }
                        title={"市町村名、日時ごとの警報基準相当"}
                        xsHeight="400px"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ height: { xs: 'auto', sm: '50%' } }}>
                    <Stack spacing={0} sx={{ height: { xs: 'auto', sm: '100%' } }}>
                        <CommonCard
                            child={
                                <ScatterRisk
                                    dateArray={dateArray}
                                    distance={riskScatterAllDatetimeCity?.length}
                                    scatterData={riskScatterAllDatetimeCity}
                                    getSeries={soilRainGetSeriesH1DcTankCity}
                                    x_ary={soil_rain_dc_tank_ary}
                                    y_ary={soil_rain_h1_ary}
                                    maxValX={200}
                                    maxValY={30}
                                    isShowLabelMaxXMm={false}
                                    scatterClickEvent={scatterClickEvent}
                                    setTooltipInfo={setTooltipInfo}
                                    tooltipInfo={tooltipInfo}
                                    renderBody={renderTooltipBody}
                                    isReadyKey={['scatter_rain_h1_dc_tank_city']}
                                    isReadyFlg={props.isReady.scatter_rain_h1_dc_tank_city}
                                    setReady={props.setReady}
                                    selectCitys={props.selectCitys}
                                    isShowDataLabel={true}
                                    height={'100%'}
                                    textX={"土壌雨量指数"}
                                    textY={"1時間雨量"}
                                    whiteSpace={'normal'}
                                    tooltipKey={"scatter_rain_h1_dc_tank_city"}
                                />
                            }
                            title={"市町村ごとの1時間最大雨量,土壤雨量指数"}
                            xsHeight="300px"
                        />
                    </Stack>
                </Grid>
            </PageLayout>
        </>
    )
}
export default Risk;